import { Either } from 'monet';

/**
 * @description Шлюз управления учетом рабочего времени сотрудников.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function WorkTimeControlsGateway(axios) {
  /**
   * @description Загрузить журнал учета рабочего времени в Excel таблицу.
   * @param {number[]} employeeIds Идентификаторы сотрудников.
   * @param {string}   startsDate  Начальная дата обзора.
   * @param {string}   endsDate    Конченая дата обзора.
   * @returns {Promise<Either>}
   */
  this.downloadWorkTimeAccountJournalExcelSpreadsheet = (
    employeeIds,
    startsDate,
    endsDate,
  ) => axios
    .get(
      `/api/work-time-account-journal/${startsDate}:${endsDate}`,
      {
        headers: { accept: 'application/vnd.ms-excel' },
        params: { employee_ids: employeeIds },
        responseType: 'blob',
      },
    )
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (
          error.isAxiosError
          && (error.response.status === 400 || error.response.status === 403)
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить последний счетчик рабочего времени.
   * @param {number} employeeId Идентификатор сотрудника.
   * @returns {Promise<Either<any,{title:string}>>}
   */
  this.removeWorkLastCounter = (employeeId) => axios
    .delete(`/api/employees/${employeeId}/work-time-counters/latest`)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Запустить новый счетчик учета времени для сотрудника.
   * @param {number}      employeeId     Идентификатор сотрудника.
   * @param {number|null} doctorAssistId Идентификатор ассистируемого доктора сотрудника.
   * @param {string}      date
   * @returns {Promise<Either<any,{title: string}>>}
   */
  this.startEmployeeWorkTimeCounter = (employeeId, doctorAssistId, date) => axios
    .post(
      `/api/employees/${employeeId}/work-time-counters`,
      {
        doctor_to_assist_id: doctorAssistId,
        date,
      },
    )
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (
          error.isAxiosError
          && (error.response.status === 400 || error.response.status === 403)
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Остановить последний счетчик учета времени для сотрудника.
   * @param {number} employeeId Идентификатор сотрудника.
   * @param {string} date       Дата остановки.
   * @returns {Promise<Either<any,{title: string}>>}
   */
  this.stopEmployeeWorkTimeCounter = (employeeId, date) => axios
    .put(`/api/employees/${employeeId}/work-time-counters/latest/stop`, { date })
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (
          error.isAxiosError
          && (error.response.status === 400 || error.response.status === 403)
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать управление рабочим временем.
   * @returns {Promise<Maybe<{employees: object[]}>>}
   */
  this.viewEmployeeWorkTimeControls = () => axios
    .get('/api/employee-work-time-controls')
    .then(
      async (response) => {
        const employees = response.data.employees.map((item) => ({
          description: item.description,
          id: item.id,
          name: item.name,
          nextAction: item.next_action,
          isAssistant: item.is_assistant,
          doctorAssist: item.doctor_assist,
          doctorsToAssist: item.doctors_to_assist.map((doctor) => ({
            name: doctor.name,
            id: doctor.id,
          })),
        }));
        return Either.Right({ employees });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать журнал учета рабочего времени.
   * @param {number[]} employeeIds Идентификаторы сотрудников.
   * @param {string}   startsDate  Начальная дата обзора.
   * @param {string}   endsDate    Конченая дата обзора.
   * @returns {Promise<Either<any,{columns:object[],employees:object[]}>>}
   */
  this.viewWorkTimeAccountJournal = (employeeIds, startsDate, endsDate) => axios
    .get(
      `/api/work-time-account-journal/${startsDate}:${endsDate}`,
      {
        headers: { accept: 'application/json' },
        params: { employee_ids: employeeIds },
      },
    )
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (
          error.isAxiosError
          && (error.response.status === 400 || error.response.status === 403)
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Показать опции журнала учета рабочего времени.
   * @returns {Promise<Either<any,{employees: object[]}>>}
   */
  this.viewWorkTimeJournalOptions = () => axios
    .get('/api/work-time-journal-options')
    .then(
      async (response) => {
        const { employees, periods, today } = response.data;
        return Either.Right({ employees, periods, today });
      },
      async (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
}
